import { useEffect } from "react";

const SITE = "https://johncampbellinsurance.com";
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

// Lightweight document-head manager: sets title, description, canonical,
// Open Graph, Twitter and JSON-LD schema per page. Cleans up on unmount.
export const Seo = ({ title, description, path = "/", image = DEFAULT_IMAGE, type = "website", jsonLd = [] }) => {
  useEffect(() => {
    const prevTitle = document.title;
    const created = [];
    const canonical = `${SITE}${path}`;

    const upsertMeta = (key, attr, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-seo", "1");
        document.head.appendChild(el);
        created.push(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, wasCreated: !el.hasAttribute("data-existing") };
    };

    if (title) document.title = title;

    const touched = [];
    const setMeta = (key, attr, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        touched.push([el, el.getAttribute("content")]);
        el.setAttribute("content", content);
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
        created.push(el);
      }
    };

    setMeta("description", "name", description);
    setMeta("og:title", "property", title);
    setMeta("og:description", "property", description);
    setMeta("og:type", "property", type);
    setMeta("og:url", "property", canonical);
    setMeta("og:image", "property", image);
    setMeta("twitter:card", "name", "summary_large_image");
    setMeta("twitter:title", "name", title);
    setMeta("twitter:description", "name", description);
    setMeta("twitter:image", "name", image);

    // canonical
    let linkCanonical = document.head.querySelector('link[rel="canonical"]');
    let prevCanonical = null;
    if (linkCanonical) {
      prevCanonical = linkCanonical.getAttribute("href");
      linkCanonical.setAttribute("href", canonical);
    } else {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      linkCanonical.setAttribute("href", canonical);
      document.head.appendChild(linkCanonical);
      created.push(linkCanonical);
    }

    // JSON-LD structured data
    const ldNodes = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "1");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      created.forEach((n) => n.remove());
      ldNodes.forEach((n) => n.remove());
      touched.forEach(([el, val]) => { if (val != null) el.setAttribute("content", val); });
      if (linkCanonical && prevCanonical != null) linkCanonical.setAttribute("href", prevCanonical);
    };
  }, [title, description, path, image, type, JSON.stringify(jsonLd)]);

  return null;
};

export default Seo;
